<template>
  <div
    v-if="repliedOnComment"
    class="comment-content__reply"
    @click="goToRepliedComment"
  >
    <BaseSubHeading
      size="small"
      weight="600"
      color="monochrome-07"
      class="comment-content__reply__owner"
    >
      {{ repliedOnComment?.owner?.fullName }}
    </BaseSubHeading>
    <div
      v-if="statusChangeComment && !repliedOnComment.deleted"
      class="comment__audit-compare-content comment-content__reply__status"
    >
      <StatusComponent
        :value="getStatusById(beforeStatus)"
        class="comment-content__reply__status-value is-opacity"
      />
      <icon
        data="@icon/arrow-to.svg"
        :fill="true"
        width="var(--icon-16)"
        height="var(--icon-16)"
        color="var(--monochrome05to04)"
      />
      <StatusComponent
        :value="getStatusById(afterStatus)"
        class="comment-content__reply__status-value"
      />
    </div>
    <BaseSubHeading
      v-if="repliedOnComment.deleted"
      size="small"
      weight="400"
      color="monochrome-05"
      darkColor="monochrome-04"
      class="comment-content__reply__deleted"
    >
      Deleted Comment
    </BaseSubHeading>
    <ReferencedText
      v-else-if="checkHtml(repliedOnComment.structuredDescription.html)"
      :html="repliedOnComment.structuredDescription.html.trim()"
      :resetStyle="true"
      class="comment-content__reply__content"
    />
    <div
      v-else-if="repliedOnComment.fileMetadata?.length"
      class="comment-content__reply__file"
    >
      <icon
        data="@icon/file.svg"
        :fill="true"
        width="var(--icon-24)"
        height="var(--icon-24)"
        color="var(--monochrome05to04)"
      />
      <div class="comment-content__reply__file-info">
        <BaseSubHeading
          size="small"
          weight="500"
          color="monochrome-07"
          class="is-ellipsis comment-content__reply__file-name"
        >
          {{ repliedOnComment.fileMetadata[0].name }}
        </BaseSubHeading>
        <BaseSubHeading
          size="small"
          weight="500"
          color="monochrome-05"
          darkColor="monochrome-04"
          class="comment-content__reply__file-size"
        >
          {{ getFileSize(repliedOnComment.fileMetadata[0].size) }}
        </BaseSubHeading>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useStore } from "vuex"

import { ReferencedText } from "@/components/Reference"
import StatusComponent from "@/components/StatusComponent/index.vue"

import formatBytes from "@/helpers/formatBytes"
import { CommentContentType, Status } from "@/models"
import { Getters } from "@/store"
import { replaceHtmlTags } from "@/utils"

import type { Comment, RepliedOnComment } from "@/models"

const props = defineProps<{
  repliedOnComment: RepliedOnComment
  originalComment?: Comment | null
}>()

const emit = defineEmits<{
  (e: "goToRepliedComment", value: string): void
}>()

const store = useStore()

const statusChangeComment = computed(() => {
  return props.originalComment?.type === CommentContentType.STATUS ? props.originalComment : null
})
const beforeStatus = computed(() => statusChangeComment.value?.before?.status ?? "")
const afterStatus = computed(() => statusChangeComment.value?.after?.status ?? "")

const checkHtml = (html: string) => {
  return !!replaceHtmlTags(html)
}

const getFileSize = (size: number) => {
  return formatBytes(size)
}

const goToRepliedComment = () => {
  if (!props.repliedOnComment || !props.repliedOnComment.id) {
    emit("goToRepliedComment", "")
    return
  }
  emit("goToRepliedComment", props.repliedOnComment.id)
}

const getStatusById = (statusId: string): Status => {
  return store.getters[Getters.GET_STATUS_BY_ID](statusId)
}
</script>
